import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddCustomerModal = ({ isOpen, onClose, onSubmit, customer = null }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    taxId: '',
    creditLimit: '',
    paymentTerms: 'net-30',
    category: 'smb',
    notes: ''
  });

  // Populate form when editing
  useEffect(() => {
    if (customer) {
      setFormData({
        companyName: customer?.companyName || '',
        contactPerson: customer?.contactPerson || '',
        email: customer?.email || '',
        phone: customer?.phone || '',
        address: customer?.address || '',
        taxId: customer?.taxId || '',
        creditLimit: customer?.creditLimit || '',
        paymentTerms: customer?.paymentTerms || 'net-30',
        category: customer?.category || 'smb',
        notes: customer?.notes || ''
      });
    } else {
      // Reset form for new customer
      setFormData({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        taxId: '',
        creditLimit: '',
        paymentTerms: 'net-30',
        category: 'smb',
        notes: ''
      });
    }
  }, [customer, isOpen]);

  const paymentTermsOptions = [
    { value: 'net-15', label: 'Net 15' },
    { value: 'net-30', label: 'Net 30' },
    { value: 'net-45', label: 'Net 45' },
    { value: 'net-60', label: 'Net 60' },
    { value: 'due-on-receipt', label: 'Due on Receipt' }
  ];

  const categoryOptions = [
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'smb', label: 'SMB' },
    { value: 'startup', label: 'Startup' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData, customer?.id);
    setFormData({
      companyName: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      taxId: '',
      creditLimit: '',
      paymentTerms: 'net-30',
      category: 'smb',
      notes: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  const isEditMode = !!customer;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
            {isEditMode ? 'Edit Customer' : 'Add New Customer'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close modal"
          />
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Business Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Company Name"
                  value={formData?.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e?.target?.value })}
                  required
                  placeholder="Enter company name"
                />

                <Input
                  label="Tax ID / EIN"
                  value={formData?.taxId}
                  onChange={(e) => setFormData({ ...formData, taxId: e?.target?.value })}
                  placeholder="XX-XXXXXXX"
                />

                <Select
                  label="Category"
                  options={categoryOptions}
                  value={formData?.category}
                  onChange={(value) => setFormData({ ...formData, category: value })}
                  required
                />

                <Input
                  label="Address"
                  value={formData?.address}
                  onChange={(e) => setFormData({ ...formData, address: e?.target?.value })}
                  placeholder="Business address"
                />
              </div>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Contact Person"
                  value={formData?.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e?.target?.value })}
                  required
                  placeholder="Full name"
                />

                <Input
                  label="Email"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  required
                  placeholder="email@company.com"
                />

                <Input
                  label="Phone"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Credit Terms</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Credit Limit"
                  type="number"
                  value={formData?.creditLimit}
                  onChange={(e) => setFormData({ ...formData, creditLimit: e?.target?.value })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />

                <Select
                  label="Payment Terms"
                  options={paymentTermsOptions}
                  value={formData?.paymentTerms}
                  onChange={(value) => setFormData({ ...formData, paymentTerms: value })}
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Notes
              </label>
              <textarea
                value={formData?.notes}
                onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                placeholder="Additional notes about this customer..."
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            iconName="Check"
            iconPosition="left"
          >
            {isEditMode ? 'Update Customer' : 'Add Customer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddCustomerModal;